import Button from '@gaia/components/Button';
import useLaunchDarkly from '@gaia/hooks/useLaunchDarkly';
import useRequestFreeTrial from '@gaia/hooks/useRequestFreeTrial';
import {useSubscriptionPlanDiscountedAmount} from '@gaia/hooks/useSubscriptionPlans';
import {useUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import {getGaiaPremiumURL} from '@gaia/urls';
import {formatPrice} from '@gaia/utils/gaia/formatPrice';
import {useMediaQuery} from '@mui/material';
import {type CSSProperties, useContext, useEffect, useMemo} from 'react';

import {useSetUpsellModalState} from './store';
import {UpsellCarouselAnalyticsFunctionContext} from './UpsellCarousel';

export default function UpsellCardButtons() {
    const user = useUser();
    const gaiaPremiumAnnualPrice = useSubscriptionPlanDiscountedAmount('GaiaGPSPremiumStripe2018');
    const launchDarklyFlags = useLaunchDarkly(user);
    const fireAnalytics = useContext(UpsellCarouselAnalyticsFunctionContext);
    const isExtraSmall = useMediaQuery('(max-width:500px)');
    const ctaSeparator = isExtraSmall ? ' ' : '-';
    const ctaText = `Upgrade to Premium ${ctaSeparator} ${formatPrice(gaiaPremiumAnnualPrice)}/yr`;

    const premiumButtonTextStyle = useMemo<CSSProperties>(
        () => ({
            whiteSpace: 'nowrap',
            ...(isExtraSmall && {
                display: 'flex',
                flexDirection: 'column',
            }),
        }),
        [isExtraSmall]
    );

    const {
        requestFreeTrial,
        isTrialRequestProcessing,
        wasFreeTrialActivated,
        requestFailureMessage,
    } = useRequestFreeTrial();

    const setUpsellModalState = useSetUpsellModalState();

    useEffect(() => {
        if (wasFreeTrialActivated) {
            setUpsellModalState(undefined);
        }
    }, [setUpsellModalState, wasFreeTrialActivated]);

    return (
        <div style={{paddingTop: theme.spacing(2)}}>
            <Button
                appearance='primary'
                fullWidth
                style={{
                    height: isExtraSmall ? theme.spacing(10) : theme.spacing(6),
                }}
                href={getGaiaPremiumURL}
                target='_blank'
                rel='noopener'
                onClick={() => fireAnalytics('Select - Upgrade to Premium - $39.99/yr')}
            >
                <span style={premiumButtonTextStyle}>{ctaText}</span>
            </Button>
            {launchDarklyFlags.allowFreeTrials && user.isEligibleForTrial && (
                <Button
                    appearance='secondary'
                    fullWidth
                    style={{
                        height: theme.spacing(6),
                        marginTop: theme.spacing(1),
                    }}
                    onClick={() => {
                        fireAnalytics('Select - Try Premium for 7 Days');
                        requestFreeTrial();
                    }}
                    disabled={isTrialRequestProcessing}
                >
                    Try Premium for 7 Days
                </Button>
            )}
            {wasFreeTrialActivated === false && (
                <span
                    style={{
                        color: theme.palette.error.main,
                        textAlign: 'center',
                        margin: '0 auto',
                        display: 'block',
                        maxWidth: 304,
                        paddingTop: theme.spacing(1),
                    }}
                >
                    {requestFailureMessage}
                </span>
            )}
        </div>
    );
}
